/**
 * src/pages/LoginPage.tsx
 * =============================================================================
 * Formulario de login. Email + password.
 *
 * Diseño: card central ancho reducido (~400px) sobre fondo neutro.
 * Sin distracciones — solo el formulario y un link a signup.
 * =============================================================================
 */

import { useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { signIn } from '@/services/authService'
import { useAuth } from '@/components/providers/AuthProvider'
import { ApiError } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'

interface LocationState {
  from?: { pathname: string }
}

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { refresh } = useAuth()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Si llegamos aquí desde un magic link de invite, tras login mandamos
  // a la página correspondiente para auto-aceptar y aterrizar en el doc
  // o espacio. Hay DOS tipos de invitación:
  //   - `invite=`       → invitación nominal a UN documento
  //   - `invite-space=` → invitación a un espacio compartido (estudios)
  // Si no hay invite, usar el state.from de React Router (RequireAuth lo
  // setea cuando un user no-auth intenta entrar a una ruta protegida).
  const inviteToken = searchParams.get('invite')
  const inviteSpaceToken = searchParams.get('invite-space')
  const redirectTo = inviteSpaceToken
    ? `/invite/space?token=${inviteSpaceToken}`
    : inviteToken
      ? `/invite/accept?token=${inviteToken}`
      : ((location.state as LocationState | null)?.from?.pathname ?? '/')

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await signIn(email, password)
      // ⚠️ IMPRESCINDIBLE: refrescar la sesión ANTES de navegar.
      //
      // `AuthProvider` se monta FUERA de `RouterProvider` (ver App.tsx), así
      // que solo consulta la sesión al montar. Un `navigate()` es navegación
      // de cliente y NO lo remonta: sin este `refresh()`, el login creaba la
      // cookie correctamente pero React seguía con `user = null` y la app
      // mostraba "Entrar" como si nadie hubiera entrado. Solo se arreglaba
      // recargando la página a mano.
      //
      // El bug pasó meses sin detectarse porque quien ya tenía cookie de una
      // sesión anterior nunca lo veía: solo aparecía en un login desde cero
      // (ventana de incógnito, o cualquier usuario nuevo). Detectado y
      // corregido el 2026-08-14.
      //
      // El orden importa: primero `refresh()` y luego `navigate()`. Ambos
      // setState caen en el mismo microtask, así que React 19 los agrupa en
      // un único render donde la ruta YA es `redirectTo`. Si se invirtiera,
      // la página destino parpadearía un instante en estado deslogueado.
      // Y si `refresh()` fuese sin `await`, `PublicOnlyRoute` podría detectar
      // la sesión antes de que navegásemos y mandarnos a `/`, perdiendo el
      // destino real (crítico en los flujos de invitación).
      await refresh()
      navigate(redirectTo, { replace: true })
    } catch (err) {
      setError(mapAuthError(err, t))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="min-h-dvh flex flex-col items-center justify-center px-6 bg-background">
      <Link to="/" className="mb-8 text-base font-bold tracking-tight">
        {t('app.name')}
      </Link>

      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-5 border border-border rounded-lg p-6 bg-card"
      >
        <div className="space-y-1">
          <h1 className="text-xl font-semibold">{t('auth:login.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('auth:login.subtitle')}</p>
        </div>

        <div>
          <Label htmlFor="email">{t('auth:login.email')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <div className="flex items-baseline justify-between">
            <Label htmlFor="password">{t('auth:login.password')}</Label>
            <Link
              to="/forgot-password"
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
            >
              {t('auth:login.forgotPassword', '¿Olvidaste tu contraseña?')}
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? t('common:actions.loading') : t('auth:login.submit')}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          {t('auth:login.noAccount')}{' '}
          <Link to="/signup" className="underline underline-offset-4 hover:text-foreground">
            {t('auth:login.signupLink')}
          </Link>
        </p>
      </form>
    </main>
  )
}

/**
 * Traduce errores típicos de Better Auth a mensajes i18n amigables.
 * Better Auth devuelve códigos semánticos en `code` cuando es ApiError.
 */
function mapAuthError(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApiError) {
    const code = err.code.toLowerCase()
    if (code.includes('invalid') && code.includes('credentials')) {
      return t('auth:errors.invalidCredentials')
    }
    if (code.includes('email_not_verified') || code.includes('verification')) {
      return t('auth:errors.emailNotVerified')
    }
    return err.message || t('common:errors.generic')
  }
  return err instanceof Error ? err.message : t('common:errors.generic')
}
